import { NumberField, ToggleField } from './fields';

export type VortexConfig = {
  token_requirement: number;
  graduation_requirement: number;
  generate_graduate_slot?: boolean;
  downline_max_level?: number;
  upline_max_level?: number;
};

export function emptyVortexConfig(): VortexConfig {
  return {
    token_requirement: 100,
    graduation_requirement: 7,
    generate_graduate_slot: true,
    downline_max_level: 0,
    upline_max_level: 0,
  };
}

/**
 * Flat Vortex config (`comp_plan_configs.vortex`). Per-tier rewards
 * (cash/GC/token grants) live on each membership tier (Members → Memberships);
 * products grant tokens via their "Vortex tokens" field.
 */
export function VortexEditor({
  value,
  onChange,
  readOnly,
}: {
  value: VortexConfig;
  onChange: (next: VortexConfig) => void;
  readOnly?: boolean;
}) {
  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <h3 className="text-sm font-medium">Queue & graduation</h3>
        <p className="text-[10px] text-muted-foreground -mt-2">
          A slot enters the Vortex queue each time its token balance covers the
          requirement (tokens come from products' "Vortex tokens" + tier
          activation grants). The oldest entry graduates when total minted
          entries reach <code className="font-mono">vortex_id × N + 1</code>
          (N = graduation requirement). Graduation pays the tier's Vortex
          reward (cash + GC).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <NumberField
            label="Token requirement (per entry)"
            hint="Tokens consumed to mint one Vortex queue slot."
            value={value.token_requirement}
            disabled={readOnly}
            onChange={(v) => onChange({ ...value, token_requirement: v ?? 0 })}
            min={0}
            step={1}
          />
          <NumberField
            label="Graduation requirement (N)"
            hint="The '7 fill → 1 graduate' number. Oldest entry graduates at vortex_id × N + 1 total entries."
            value={value.graduation_requirement}
            disabled={readOnly}
            onChange={(v) => onChange({ ...value, graduation_requirement: Math.max(1, v ?? 1) })}
            min={1}
            step={1}
          />
        </div>
        <ToggleField
          label="Generate re-entry slot on graduation"
          hint="When a slot graduates, mint a fresh queue entry so it re-cycles."
          disabled={readOnly}
          value={value.generate_graduate_slot ?? false}
          onChange={(on) => onChange({ ...value, generate_graduate_slot: on })}
        />
      </section>

      {/* Cycle income depth (downline/upline) is hidden for now. The config
          values are still preserved on save; restore this <section> to re-expose
          the editor. The engine reads downline_max_level / upline_max_level and
          stays inert while both are 0 (the default). */}
    </div>
  );
}
